import React from "react"

export function MemberDesignModal({ selectedStudio, studioMembersWithDesigns, designThumbnails, onPreviewDesign, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{selectedStudio?.name} - Member Design Status</h2>
              <p className="text-sm text-gray-600 mt-1">Recital book preparation - Track member design submissions</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {studioMembersWithDesigns.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading member information...</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Header Row */}
              <div className="grid grid-cols-10 gap-4 p-4 bg-gray-50 border-b border-gray-200 font-medium text-sm text-gray-700">
                <div className="col-span-4">Member</div>
                <div className="col-span-2">Last Updated</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Designs</div>
              </div>

              {/* Member Rows */}
              {studioMembersWithDesigns.map(member => (
                <div key={member.id} className="grid grid-cols-10 gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  {/* Member Info */}
                  <div className="col-span-4 flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-gray-600">
                          {member.displayName?.charAt(0) || member.email?.charAt(0) || "?"}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{member.displayName || member.email}</h3>
                      <p className="text-xs text-gray-500 truncate">{member.email}</p>
                    </div>
                  </div>

                  {/* Last Updated */}
                  <div className="col-span-2 flex items-center">
                    <div className="text-sm text-gray-600">
                      {member.designs.length > 0 ? (
                        (() => {
                          const mostRecent = member.designs.reduce((latest, design) => {
                            if (!latest.updatedAt) return design
                            if (!design.updatedAt) return latest
                            return design.updatedAt.toDate() > latest.updatedAt.toDate() ? design : latest
                          })
                          return (
                            <div>
                              <div>{mostRecent.updatedAt?.toDate ? mostRecent.updatedAt.toDate().toLocaleDateString() : "Unknown"}</div>
                              <div className="text-xs text-gray-400">
                                {mostRecent.updatedAt?.toDate
                                  ? mostRecent.updatedAt.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                  : ""}
                              </div>
                            </div>
                          )
                        })()
                      ) : (
                        <span className="text-gray-400">No designs</span>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-span-2 flex items-center">
                    {member.hasDesigns ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Submitted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        ⚠ Pending
                      </span>
                    )}
                  </div>

                  {/* Design Thumbnails */}
                  <div className="col-span-2 flex items-center">
                    {member.hasDesigns ? (
                      <div className="flex space-x-2">
                        {member.designs.slice(0, 3).map(design => (
                          <div key={design.id} className="flex flex-col items-center space-y-1">
                            <div
                              className={`relative border rounded-md overflow-hidden ${
                                design.isPreferred ? "border-purple-300 ring-1 ring-purple-200" : "border-gray-200"
                              }`}
                              style={{ width: "60px", height: "45px" }}
                            >
                              {designThumbnails[design.id] ? (
                                <img
                                  src={designThumbnails[design.id]}
                                  alt={design.name || "Design"}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                  <div className="text-gray-400 text-xs">...</div>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center min-h-8 gap-2 space-x-1">
                              {design.isPreferred && (
                                <div className="text-lg" title="Preferred for recital book">
                                  ⭐
                                </div>
                              )}
                              <button
                                onClick={() => onPreviewDesign(design)}
                                className="text-gray-500 hover:text-blue-600 transition-colors"
                                title={`Preview: ${design.name || "Untitled Design"}`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                        {member.designs.length > 3 && (
                          <div className="flex items-center justify-center w-12 h-8 text-xs text-gray-500 bg-gray-100 rounded border border-gray-200">
                            +{member.designs.length - 3}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No designs</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

